const ARTICLES_2 = [
  {
    slug: "how-to-choose-a-security-company-england",
    title: "How to Choose a Security Company in England: 9 Checks That Matter",
    description: "Every security company claims to be the best. Nine practical checks — from SIA licences to patrol verification — that separate professionals from pretenders.",
    category: "Guides",
    date: "2026-07-15",
    readMins: 7,
    intro: [
      "Open ten security company websites and you will read the same words ten times: professional, reliable, trusted, tailored. The words are free — the proof is not. Here are the nine checks that actually separate a dependable security provider from an expensive disappointment.",
    ],
    sections: [
      {
        h: "Start with the legal basics",
        paras: [
          "Every officer must hold a valid SIA licence for their role — ask for licence numbers and verify them on the SIA public register. Then ask about vetting: the industry benchmark is BS 7858 background screening, and serious providers will show you evidence for the officers assigned to your contract.",
          "Insurance is the third non-negotiable. Public liability cover of at least £5m (many clients require £10m) should be evidenced with a certificate, not a sentence on a website.",
        ],
      },
      {
        h: "Look for proof systems, not promises",
        paras: [
          "The single best predictor of a good security company is how it proves delivery. Ask how patrols are verified — you want GPS-tracked visits, NFC or QR checkpoint scans and time-stamped photographic reports — and ask to see a sample report from a real contract.",
          "If a provider cannot show you what you will receive every week or month, you will be buying trust rather than security. Verification technology is cheap and universal now; its absence is a choice.",
        ],
      },
      {
        h: "Interrogate the mobilisation plan",
        paras: [
          "How a company starts a contract tells you how it will run one. Ask: who writes the assignment instructions, when do officers get briefed on my site, and what happens in week one? A rushed start with no site survey produces generic guarding that never improves.",
          "Ask also about continuity: what happens when your regular officer is ill, on holiday, or leaves? The answer should be a named relief pool and supervisor cover, not a shrug.",
        ],
      },
      {
        h: "Compare management, not just officers",
        paras: [
          "Officers deliver the service; management determines whether it stays good. You want a named contract manager, scheduled performance reviews, clear KPIs and an escalation route that reaches a decision-maker in hours, not days.",
          "Xtreme Protection runs every contract in England this way — free risk assessment, written plan, verified delivery, monthly review. Call +44 2046 402840 for a same-day quote you can hold us to.",
        ],
      },
    ],
    faqs: [
      {
        q: "How many security quotes should I get?",
        a: "Two or three is ideal. More than that and you are comparing documents rather than providers. Give each bidder the same information and a site visit, then compare on proof systems, management and total cost — not the headline rate.",
      },
      {
        q: "Is a local security company better than a national one?",
        a: "What matters is local delivery with national backup. Pure local firms can struggle with cover and surge capacity; pure nationals can deliver generic service. Look for locally based officers supervised within a national command structure.",
      },
      {
        q: "What should a security contract include?",
        a: "Written assignment instructions, named officers, vetting evidence, verification and reporting arrangements, KPIs, review schedule, notice period and a clear rate card for variations. If any of these are missing, ask why.",
      },
    ],
    relatedServices: ["manned-guarding", "corporate-security", "key-holding-alarm-response"],
    relatedCities: ["london", "birmingham", "manchester"],
  },
  {
    slug: "what-do-door-supervisors-do",
    title: "What Door Supervisors Actually Do (and What They Cannot Do)",
    description: "The real job of an SIA door supervisor: entry control, searches, refusals, ejections and incident handling — plus the legal limits every venue should know.",
    category: "Guides",
    date: "2026-07-15",
    readMins: 6,
    intro: [
      "To most customers, a door supervisor is the person deciding who gets in. To a venue operator, they are the front line of your premises licence. Understanding what door staff actually do — and where their legal powers stop — makes for safer venues and fewer licensing headaches.",
    ],
    sections: [
      {
        h: "The legal role at licensed premises",
        paras: [
          "Anyone working the door at licensed premises in England must hold an SIA door supervisor licence — this applies to employed staff as well as contractors, which catches out venues that assume in-house bouncers are exempt.",
          "The licence exists because the role involves judgment calls with legal consequences: refusing entry, conducting searches, managing capacity and physically ejecting people when necessary.",
        ],
      },
      {
        h: "A night on the door",
        paras: [
          "A professional door team works a system, not a hunch: queue management and ID checks, search policies applied consistently, clicker or electronic capacity counts, radio contact with the venue manager, and early intervention when a situation inside starts to build.",
          "The best door supervisors prevent rather than react. A calm word at the queue, a refused sale flagged to bar staff, a group separated before a flashpoint — most of the value never becomes an incident log entry.",
        ],
      },
      {
        h: "Where the powers stop",
        paras: [
          "Door supervisors have no special police powers. They can refuse entry and ask people to leave; they can use reasonable force to eject someone who refuses; and they can make a citizen's arrest for indictable offences. They cannot search anyone without consent, and force must be proportionate — excessive force is assault whoever employs them.",
          "This is why training and supervision matter. An untrained door team creates assault allegations, licensing reviews and compensation claims; a professional one creates a venue people feel safe returning to.",
        ],
      },
      {
        h: "Hiring a door team that protects your licence",
        paras: [
          "Ask for SIA licence numbers, evidence of incident logging (body-worn video is increasingly standard), and how the provider briefs teams on your venue's specific policies — drugs, searches, refusals, vulnerable customers.",
          "Xtreme Protection supplies licensed door teams to pubs, bars, clubs and venues across England. Call +44 2046 402840 to discuss your venue.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can door supervisors search customers?",
        a: "Only with consent — a search is a condition of entry, never a right. A customer who refuses can be turned away but cannot be searched by force.",
      },
      {
        q: "Can door staff refuse entry without giving a reason?",
        a: "A venue can refuse entry to anyone, but refusal must not be discriminatory on protected characteristics. Professional teams log refusals with reasons to protect both the venue and the customer.",
      },
      {
        q: "How many door supervisors does my venue need?",
        a: "It depends on capacity, layout, clientele and hours — your licensing conditions may specify numbers. A risk assessment by an experienced provider will recommend staffing per night of the week.",
      },
    ],
    relatedServices: ["door-supervision", "event-security", "hotel-security"],
    relatedCities: ["manchester", "liverpool", "newcastle-upon-tyne"],
  },
  {
    slug: "cctv-monitoring-vs-guards",
    title: "CCTV Monitoring vs Security Guards: An Honest Comparison",
    description: "Cameras record and detect; guards intervene and decide. How to weigh remote CCTV monitoring against manned guarding for your site — and when you need both.",
    category: "Guides",
    date: "2026-07-16",
    readMins: 6,
    intro: [
      "It is the defining budget question in modern security: invest in people on the ground or technology in the sky? The honest answer is that cameras and guards do different jobs — and understanding the difference is how you avoid paying for the wrong one.",
    ],
    sections: [
      {
        h: "What cameras do brilliantly",
        paras: [
          "Monitored CCTV never blinks, never takes a break and sees the whole site at once. Detection analytics spot movement at 200 metres in the dark; operators issue live audio warnings the moment a perimeter is breached; and every incident is recorded for insurers, police and court.",
          "Cameras also scale without limit. One monitoring centre can watch fifty sites simultaneously, which is why monitored towers have transformed construction and vacant property protection.",
        ],
      },
      {
        h: "What guards do that cameras cannot",
        paras: [
          "A camera records a crime; a guard interrupts it. Officers challenge, escort, search, unlock, investigate and make judgment calls no algorithm can. They control access, manage visitors and contractors, and provide the human presence that staff and customers actually feel.",
          "Guards are also evidence in themselves: a uniformed officer on patrol is a visible deterrent that changes a criminal's risk calculation before any technology activates.",
        ],
      },
      {
        h: "The cost and coverage maths",
        paras: [
          "Continuous guarding covers one site with one officer; monitoring covers your whole site at a fraction of the hourly cost. For a large, low-traffic site — a void, a yard, a development — monitored CCTV usually wins on pure economics.",
          "For premises with people, access points and public interaction — receptions, retail, venues — technology cannot replace the human layer. The question shifts from which to how much of each.",
        ],
      },
      {
        h: "The hybrid most sites end up with",
        paras: [
          "The strongest setups combine them: monitored cameras watch the perimeter, a guard or patrol responds to what they see, and key holding means an activation never disturbs your sleep. Detection, response and evidence in one loop.",
          "Xtreme Protection designs hybrid CCTV and guarding packages across England, with monitoring linked directly to our response units. Call +44 2046 402840 for a free site assessment.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is monitored CCTV cheaper than a security guard?",
        a: "For overnight and out-of-hours cover, usually significantly so — monitoring spreads operator cost across many sites. The trade-off is that cameras detect and record but cannot physically intervene, which is why most monitored sites pair CCTV with a response contract.",
      },
      {
        q: "Do cameras actually deter crime?",
        a: "Visible, obviously-monitored cameras do deter opportunists, especially with audio challenge capability. Determined criminals treat unmonitored cameras as a recording device for their trial they plan never to attend — monitoring is what changes the calculation.",
      },
      {
        q: "Can a monitoring centre dispatch guards to my site?",
        a: "Yes — that is exactly how monitored systems should work. Verified alarms trigger a keyholding and response unit, so a human attends when the cameras see something real.",
      },
    ],
    relatedServices: ["cctv-monitoring", "manned-guarding", "mobile-security-patrols"],
    relatedCities: ["leeds", "sheffield", "bristol"],
  },
  {
    slug: "hotel-security-guide",
    title: "Hotel Security: Protecting Guests Without Spoiling the Stay",
    description: "Hotel security is hospitality with teeth. How hotels across England protect guests, staff and reputations with discreet, professional security teams.",
    category: "Sector Guides",
    date: "2026-07-16",
    readMins: 6,
    intro: [
      "A hotel is a strange security environment: open to the public, full of strangers, operating all night — yet expected to feel like a home. Getting hotel security right means protecting people and property without ever making a guest feel watched.",
    ],
    sections: [
      {
        h: "Mapping the hotel risk landscape",
        paras: [
          "The predictable pressure points are the car park, the reception desk at 2am, function rooms during events, and the corridors and fire exits of the small hours. Add cash handling, guest property, VIP visits and the occasional difficult eviction, and the risk map fills quickly.",
          "Most hotel incidents are low-level but high-stakes: noise complaints that escalate, unauthorised visitors, intoxicated guests, and thefts from rooms and vehicles that turn into one-star reviews.",
        ],
      },
      {
        h: "Discretion as the operating standard",
        paras: [
          "Hotel security officers must look like hospitality and think like security. Presentation standards match the brand — suit rather than stab vest in a five-star property — and interventions start at conversation level, with physical response as the last resort.",
          "The overnight shift is where the model earns its keep: corridor patrols, noise management, welfare checks, and a calm professional presence when a guest is in distress at 3am.",
        ],
      },
      {
        h: "Events, weddings and VIPs",
        paras: [
          "Function spaces multiply risk: a wedding for 200 brings alcohol, emotions and unfamiliar faces into a residential building. Dedicated event security for functions — with its own brief, posts and radio channel — keeps the celebration and the sleeping guests safely separate.",
          "High-profile guests add another layer: discreet close protection, controlled access to floors, and coordination with the guest's own team where they travel with one.",
        ],
      },
      {
        h: "Choosing a hotel security provider",
        paras: [
          "Ask for hospitality experience specifically, presentation standards in writing, and incident reporting that respects guest confidentiality while giving management the full picture. The provider should feel like part of the guest services team, not an occupying force.",
          "Xtreme Protection provides hotel and hospitality security across England — from boutique properties to resort complexes. Call +44 2046 402840 to talk through your property.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do hotel security officers have to be in uniform?",
        a: "No — many hotels prefer suited officers who blend with front-of-house standards. The right presentation depends on your brand and guest profile, and should be agreed in the assignment instructions.",
      },
      {
        q: "Can we have cover only overnight and at weekends?",
        a: "Yes — flexible patterns are common in hospitality. Many hotels run security through the night and at event times only, scaling up for functions and peak seasons.",
      },
      {
        q: "How do officers handle difficult guests?",
        a: "Verbal de-escalation first, always. Officers are trained in conflict management, work closely with the duty manager, and escalate to refusal of service or police only when other options are exhausted.",
      },
    ],
    relatedServices: ["hotel-security", "concierge-security", "event-security"],
    relatedCities: ["london", "brighton-and-hove", "bath"],
  },
  {
    slug: "warehouse-security-guide",
    title: "Warehouse Security: Stopping the Theft You Never See",
    description: "Warehouse theft is often internal and invisible until the stocktake. How gatehouse discipline, load checks and layered security protect distribution centres.",
    category: "Sector Guides",
    date: "2026-07-17",
    readMins: 7,
    intro: [
      "The theft that hurts a warehouse most is rarely a midnight break-in. It is the slow bleed: pallets that leave with the wrong manifest, returns that never make it back to stock, and the quiet collusion that only surfaces when the numbers refuse to reconcile. Warehouse security has to watch the gate, the floor and the paperwork at once.",
    ],
    sections: [
      {
        h: "The three channels of warehouse loss",
        paras: [
          "External theft gets the headlines — yard intrusions, trailer theft, the weekend break-in. Internal theft does the cumulative damage: staff theft alone or with drivers, picking manipulation and refund fraud. And in-transit loss sits between the two, where loads are diverted or skimmed between your dock and the destination.",
          "Each channel needs different controls, which is why a single measure — a camera, a guard, a fence — never solves warehouse shrinkage on its own.",
        ],
      },
      {
        h: "Gatehouse discipline: where control begins",
        paras: [
          "The gatehouse is the warehouse's immune system. Every vehicle booked in against a reference, every driver's identity verified, every seal checked and recorded, every trailer searched or sealed on exit according to policy. When gatehouse discipline slips, everything downstream unravels.",
          "Professional gatehouse officers also manage the flow — visitor passes, contractor control, delivery scheduling — keeping the operation moving while keeping it honest.",
        ],
      },
      {
        h: "Layering the site: perimeter, eyes and patrols",
        paras: [
          "Beyond the gate: a maintained perimeter with lighting and clear lines of sight, monitored CCTV covering yards, docks and fuel stores, and patrols at random intervals through the night. Load integrity checks — random physical verification of outbound loads against manifests — catch what the paperwork hides.",
          "Verification matters here as everywhere: GPS-tracked patrols and photographic reports give your loss prevention team evidence, not anecdotes.",
        ],
      },
      {
        h: "Proving the value in the numbers",
        paras: [
          "Warehouse security should report like an operations function: incidents, interventions, gatehouse throughput, check compliance and shrinkage trend. When security can show its impact on the loss line, it stops being a cost and starts being an investment.",
          "Xtreme Protection protects distribution centres, fulfilment hubs and industrial sites across England. Call +44 2046 402840 for a free site security review.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can warehouse security search staff?",
        a: "Only where a clear, lawful search policy exists in employment contracts and is applied consistently — typically random bag checks on exit. Officers conduct searches with consent, a witness, and full documentation.",
      },
      {
        q: "What is a load integrity check?",
        a: "A physical check that an outbound load matches its manifest — seals intact, pallet counts correct, nothing added or removed. Randomised checks are a powerful deterrent to internal and in-transit theft.",
      },
      {
        q: "Do we need guards if we already have CCTV?",
        a: "Cameras record and detect; they cannot stop a vehicle at the gate or verify a seal. Most distribution centres need both — monitored cameras for coverage, officers for control and intervention.",
      },
    ],
    relatedServices: ["warehouse-industrial-security", "cctv-monitoring", "manned-guarding"],
    relatedCities: ["northampton", "milton-keynes", "coventry"],
  },
  {
    slug: "k9-security-when-to-use",
    title: "K9 Security: When a Dog Team Beats a Second Guard",
    description: "Security dog units cover ground, detect intruders and deter like nothing else. When K9 security makes sense — and the standards a provider should meet.",
    category: "Guides",
    date: "2026-07-17",
    readMins: 5,
    intro: [
      "There is a reason intruders who would happily test a fence will not test a dog. A trained security dog hears and smells what no human can, covers open ground at speed, and presents a psychological deterrent that is worth several uniformed officers on the right site.",
    ],
    sections: [
      {
        h: "What a patrol dog actually adds",
        paras: [
          "A general-purpose patrol dog extends its handler's senses across hundreds of metres: detecting intruders by scent and sound long before visual contact, flushing hiding places, and holding ground that would take multiple officers to cover. On large, dark, open sites, one K9 unit can genuinely outperform two or three guards.",
          "The deterrent effect is immediate and measurable — sites that suffer repeat intrusions typically see attempts stop entirely once dog patrols are visibly established.",
        ],
      },
      {
        h: "Where K9 units earn their keep",
        paras: [
          "Construction and demolition sites, vacant land and buildings, scrapyards and recycling centres, utility compounds and large distribution yards — anywhere with wide perimeters, poor sightlines and determined intruders. K9 teams also excel at rapid response to alarm activations in high-risk areas.",
          "Detection dogs are the other discipline: trained to find drugs, explosives or pyrotechnics at venues, festivals and events, screening far faster than any manual search.",
        ],
      },
      {
        h: "The standards behind the leash",
        paras: [
          "Professional K9 security is tightly governed. Handlers should be SIA licensed and trained to NASDU standards; dogs must be trained, socialised and welfare-managed under veterinary oversight; and vehicles, equipment and insurance must be specific to dog work.",
          "Ask any provider for handler licence numbers, NASDU certification and welfare arrangements. A dog unit without documented standards is a liability on a leash.",
        ],
      },
      {
        h: "K9 as part of a layered plan",
        paras: [
          "Dog teams work best in combination: K9 patrols sweeping the perimeter, monitored CCTV covering the approaches, and static or mobile officers controlling the gates. Each layer makes the others more effective.",
          "Xtreme Protection deploys NASDU-standard K9 units across England for high-risk sites and specialist detection work. Call +44 2046 402840 to discuss whether a dog team fits your risk.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do K9 handlers need an SIA licence?",
        a: "Yes — dog handlers performing security work must hold an SIA licence like any other officer, and professional handlers also carry NASDU or equivalent K9-specific certification.",
      },
      {
        q: "Are security dogs safe around the public?",
        a: "Trained patrol dogs work under strict handler control and are deployed on sites and perimeters, not in crowded public spaces. For public environments, specialist detection dogs with specific temperaments are used instead.",
      },
      {
        q: "Is a dog unit cheaper than two guards?",
        a: "On large open sites, frequently yes — one K9 unit covers ground that would otherwise need two or three officers, and the deterrent effect is stronger. For indoor or public-facing roles, guards remain the right choice.",
      },
    ],
    relatedServices: ["k9-security-dog-handlers", "vacant-property-security", "construction-site-security"],
    relatedCities: ["birmingham", "middlesbrough", "doncaster"],
  },
  {
    slug: "corporate-front-of-house-security",
    title: "Front-of-House Security: The Face of Your Building",
    description: "Corporate security officers are your brand's first impression and its last line of defence. What professional front-of-house security actually delivers.",
    category: "Sector Guides",
    date: "2026-07-18",
    readMins: 6,
    intro: [
      "The first person a visitor meets in your building is rarely your receptionist — it is security. Corporate front-of-house officers carry a double responsibility: the warmth of a five-star welcome and the vigilance of a control point, delivered simultaneously.",
    ],
    sections: [
      {
        h: "Security as brand experience",
        paras: [
          "A well-run front of house sets the tone for the whole building: confident, polished officers who greet by name, issue passes efficiently and manage the lobby's rhythm. Tenants and clients judge a building's quality in its first thirty seconds — and security owns those seconds.",
          "This is why corporate security recruits differently: presentation, communication and emotional intelligence sit alongside SIA licensing as core requirements.",
        ],
      },
      {
        h: "The daily discipline",
        paras: [
          "Behind the welcome sits a rigorous routine: access control and pass management, visitor pre-registration, contractor permits, mail and package screening, key and card management, CCTV oversight and the front-line response to every alarm in the building.",
          "Officers also carry the building's emergency procedures — fire panel response, evacuation coordination, first aid — making them the operational hub when anything goes wrong.",
        ],
      },
      {
        h: "Tailgating: the breach nobody sees",
        paras: [
          "The most common corporate security failure is also the most polite: someone holding a door. Tailgating defeats expensive access systems through simple courtesy, and only attentive officers — backed by a challenge culture staff actually accept — keep it in check.",
          "Good front-of-house teams make challenging strangers feel normal rather than confrontational: a smile, a question, a pass check. Security delivered as service.",
        ],
      },
      {
        h: "What to expect from a provider",
        paras: [
          "Insist on officers recruited for corporate environments, site-specific training, grooming and presentation standards, and reporting that covers the building's daily operations, not just incidents. Continuity matters enormously — the value of an officer who knows your people compounds monthly.",
          "Xtreme Protection provides corporate and front-of-house security to offices, headquarters and campuses across England. Call +44 2046 402840 for a proposal built around your building.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can security officers take on reception or admin tasks?",
        a: "Yes — hybrid front-of-house roles combining security duties with visitor management, meeting room support and switchboard tasks are common. The split should be written into the assignment instructions so neither function suffers.",
      },
      {
        q: "Uniform or suit for corporate security?",
        a: "Your choice. Most corporate clients prefer suited officers who match the building's brand; some prefer a visible uniform for deterrence. Both are possible within the same contract.",
      },
      {
        q: "Is one officer enough for a corporate reception?",
        a: "For a single-entrance building with moderate footfall, usually yes — with patrol and lock-up duties scheduled around reception cover. Larger campuses typically need a small team with a supervisor.",
      },
    ],
    relatedServices: ["corporate-security", "concierge-security", "key-holding-alarm-response"],
    relatedCities: ["london", "reading", "manchester"],
  },
  {
    slug: "school-security-safeguarding",
    title: "School Security: Balancing Safety, Safeguarding and Welcome",
    description: "Schools need security that protects without feeling like a fortress. How safeguarding-trained officers manage gates, visitors and out-of-hours risk.",
    category: "Sector Guides",
    date: "2026-07-18",
    readMins: 6,
    intro: [
      "A school is the hardest place to secure well: it must be open enough to feel welcoming to six-year-olds and their parents, yet controlled enough that no unauthorised adult ever reaches a child. That tension defines education security — and it is why the people matter more than the hardware.",
    ],
    sections: [
      {
        h: "The risks schools actually face",
        paras: [
          "The headline fear is intruders, and gates and visitor management address it. But the daily reality is broader: custody-related flashpoints at the gates, trespass and vandalism after hours, theft of equipment during holidays, and antisocial behaviour around the perimeter at the end of the day.",
          "Each risk sits at a different time on the clock, which is why school security needs a pattern — drop-off, school day, clubs, evening lettings, weekends and holidays all carry different exposure.",
        ],
      },
      {
        h: "Safeguarding-first officers",
        paras: [
          "Security staff in schools must be DBS-checked and safeguarding-trained as the baseline, and chosen for temperament: calm with distressed children, patient with parents, and clear-eyed about boundaries. The officer who is excellent at a distribution centre may be entirely wrong for a primary school gate.",
          "Officers work within the school's safeguarding structure, reporting concerns to the designated lead and understanding that in education, observation and escalation are often the job.",
        ],
      },
      {
        h: "Gates, visitors and the school day",
        paras: [
          "The morning and afternoon gate is the critical control point: a visible, known officer managing entry, checking late arrivals and challenging unknown adults — warmly but firmly. Visitor management then takes over inside: sign-in, lanyards, escorting where required.",
          "Evening lettings, parents' evenings and school events need their own cover, and holidays need a different model entirely: the empty school becomes a vacant property risk, best met with patrols and alarm response rather than a daily presence.",
        ],
      },
      {
        h: "Security that fits a school budget",
        paras: [
          "Few schools can fund 24/7 guarding, and most do not need it. A blended pattern — gate duty at peak times, patrols out of hours, response cover through the night and holiday inspection regimes — delivers the protection within realistic budgets.",
          "Xtreme Protection provides safeguarding-trained officers to schools, colleges and universities across England. Call +44 2046 402840 to discuss a pattern that fits your site and budget.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do school security officers need DBS checks?",
        a: "Yes — anyone working regularly in a school should hold an enhanced DBS check, plus safeguarding training appropriate to education settings. This sits alongside their SIA licence and BS 7858 vetting.",
      },
      {
        q: "Can security work during the school day without alarming children?",
        a: "Yes — experienced education officers become a familiar, trusted presence. Presentation is adjusted (hi-vis only where useful), and officers are introduced to pupils and staff so they read as part of the school team.",
      },
      {
        q: "What cover do schools need during holidays?",
        a: "Empty school buildings attract trespass, vandalism and metal theft. A mix of mobile patrols, alarm response with keyholding, and scheduled inspections is the usual answer — costed as a holiday package rather than daily guarding.",
      },
    ],
    relatedServices: ["student-campus-security", "mobile-security-patrols", "key-holding-alarm-response"],
    relatedCities: ["nottingham", "leicester", "derby"],
  },
  {
    slug: "hospital-security-challenges",
    title: "Hospital Security: Protecting Staff Without Restricting Care",
    description: "Violence against NHS staff is rising. How specialist healthcare security officers protect hospitals with compassion, conflict skills and calm.",
    category: "Sector Guides",
    date: "2026-07-19",
    readMins: 6,
    intro: [
      "A hospital never closes, never turns anyone away, and deals with people at the worst moments of their lives. That makes healthcare the most demanding environment a security officer can work — where the job is to keep everyone safe while treating every single person, including the aggressive ones, as a patient first.",
    ],
    sections: [
      {
        h: "The scale of the problem",
        paras: [
          "Violence and aggression against NHS staff runs at tens of thousands of reported incidents a year in England, concentrated in emergency departments, mental health settings and anywhere long waits meet high emotion. Security in hospitals is not about theft — it is about keeping care possible.",
          "Staff who feel unsafe leave; departments that feel unsafe lose them faster. Effective security is now understood as a retention and patient-safety measure, not a facilities cost.",
        ],
      },
      {
        h: "Conflict resolution first, restraint last",
        paras: [
          "Healthcare security officers are trained differently: conflict resolution and de-escalation as the core skill, mental health and dementia awareness as standard, and physical intervention as a last resort conducted within strict legal and clinical frameworks.",
          "The goal is always the same: a ward that stays calm, a patient who stays safe, and staff who can keep treating people. Most shifts, that is achieved with words.",
        ],
      },
      {
        h: "A&E on a Friday night",
        paras: [
          "The emergency department after dark is the sharpest test: intoxication, mental health crisis, frustration and fear in one waiting room. Experienced officers read the room constantly — positioning, early engagement, quiet separation of brewing conflicts — so that flashpoints dissolve before staff ever see them.",
          "Beyond A&E, officers support wards with challenging patients, escort vulnerable people, manage car parks and site traffic, and respond to every staff panic alarm in the building.",
        ],
      },
      {
        h: "Working inside the clinical team",
        paras: [
          "Hospital security only works integrated: liaison with safeguarding leads, psychiatric liaison teams, police and ambulance crews, all within patient confidentiality rules. Officers are part of the care environment, not policed on top of it.",
          "Xtreme Protection provides healthcare-trained security officers to NHS trusts, private hospitals and clinics across England. Call +44 2046 402840 to discuss your setting.",
        ],
      },
    ],
    faqs: [
      {
        q: "Are hospital security officers mental health trained?",
        a: "They should be. Specialist healthcare officers receive mental health awareness, dementia awareness and conflict resolution training beyond the standard SIA syllabus — ask any provider for evidence of healthcare-specific training.",
      },
      {
        q: "Can security officers restrain patients?",
        a: "Physical intervention in healthcare settings is a last resort, used only to prevent harm and within strict legal and clinical guidelines. Officers trained in restraint-reduction approaches exhaust every verbal option first.",
      },
      {
        q: "Can you cover just nights and weekends?",
        a: "Yes — many hospitals supplement NHS teams with contracted officers for peak-risk periods: evenings, nights, weekends and seasonal pressures. Patterns are built from your incident data.",
      },
    ],
    relatedServices: ["healthcare-security", "manned-guarding", "cctv-monitoring"],
    relatedCities: ["leeds", "bristol", "newcastle-upon-tyne"],
  },
  {
    slug: "student-accommodation-security",
    title: "Student Accommodation Security: A Guide for PBSA Operators",
    description: "Purpose-built student accommodation has a unique risk profile: parcels, parties, tailgating and welfare. How the concierge-security hybrid solves it.",
    category: "Sector Guides",
    date: "2026-07-19",
    readMins: 6,
    intro: [
      "Purpose-built student accommodation is hospitality, housing and security in one building: hundreds of young people, many living away from home for the first time, in a block that never really sleeps. The operators getting it right have moved past the guard-or-receptionist choice to a genuinely hybrid model.",
    ],
    sections: [
      {
        h: "The PBSA risk profile",
        paras: [
          "The daily pattern is predictable: parcel mountains, visitors and tailgating, parties and noise, and fire doors propped for convenience. Around it sit the sharper risks: unauthorised access, theft from rooms and communal areas, and — most importantly — student welfare crises in the small hours.",
          "Freshers' weeks, exam seasons and end-of-term departures each reshape the risk again, so a static annual plan never survives contact with October.",
        ],
      },
      {
        h: "The concierge-security hybrid",
        paras: [
          "The model that works combines concierge service with security authority: officers who sign for parcels, help locked-out students and know residents by name — while controlling access, challenging tailgaters and running night patrols of corridors, common rooms and car parks.",
          "Because the same people deliver welcome and enforcement, the building feels like a community rather than a controlled facility — and incidents get reported to officers students actually trust.",
        ],
      },
      {
        h: "Welfare-aware staffing",
        paras: [
          "Student accommodation officers meet mental health crises, intoxication and vulnerability as a matter of routine. Welfare-aware training — recognising distress, responding appropriately, escalating to university support services — is as essential as the SIA licence.",
          "DBS checks, safeguarding awareness and clear reporting lines to the operator's pastoral team should be contractual, not aspirational.",
        ],
      },
      {
        h: "Building the right cover pattern",
        paras: [
          "Most PBSA sites concentrate cover on the evening-night window with concierge presence through the day, scaling up for freshers and key dates, and switching to patrol-and-response models over the summer void.",
          "Xtreme Protection provides student accommodation and campus security across England, with welfare-aware, DBS-checked officers. Call +44 2046 402840 to discuss your portfolio.",
        ],
      },
    ],
    faqs: [
      {
        q: "Concierge or security guard — which does student housing need?",
        a: "Usually both functions in one role. The concierge-security hybrid delivers service (parcels, queries, lockouts) and protection (access control, patrols, incident response) without doubling the wage bill.",
      },
      {
        q: "Do officers in student accommodation need DBS checks?",
        a: "It is strongly recommended and increasingly expected by operators and universities, given the regular contact with young people — alongside SIA licensing and BS 7858 vetting.",
      },
      {
        q: "How do you handle parties and noise complaints?",
        a: "Officers respond with proportionate, documented interventions: engagement first, escalation to management for repeat issues, and clear incident records that support tenancy action where needed.",
      },
    ],
    relatedServices: ["student-campus-security", "concierge-security", "mobile-security-patrols"],
    relatedCities: ["nottingham", "sheffield", "liverpool"],
  },
];

export default ARTICLES_2;
